// © 2013 - 2022 Tinder, Inc., ALL RIGHTS RESERVED

import { cx } from '@linaria/core';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import TextareaAutosize from 'react-autosize-textarea';

import { TextFieldElement, BaseTextFieldInputProps } from './types';

import styles, { theme } from './styles';

import { useKeyboardFocus, useObjectState } from '../../utils/hooks';

export const BaseTextFieldInput: React.ForwardRefRenderFunction<
  TextFieldElement,
  BaseTextFieldInputProps
> = (props, ref) => {
  const {
    // react
    className,
    defaultValue,
    type,
    placeholder,
    // config
    multiline,
    minHeight,
    maxHeight,
    minRows = 1,
    maxRows,
    readOnly,
    value: propValue,
    format,
    parse,
    allowEmptyNumber = true,
    processOnEnter,
    // colors
    background,
    border,
    text,
    textPlaceholder,
    icon,
    focus,
    backgroundDisabled,
    borderDisabled,
    borderFocus,
    borderError,
    textDisabled,
    textPlaceholderDisabled,
    iconDisabled,
    // state
    disabled,
    hasError,
    // ui state
    overrideFocused,
    // handlers
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    ...restProps
  } = props;

  const inputRef = useRef<TextFieldElement>(null);
  useImperativeHandle(ref, () => inputRef.current);

  const processValue = useCallback(
    (inputValue: string) => {
      const parsedValue = parse ? parse(inputValue) : inputValue;
      if (allowEmptyNumber && (parsedValue == null || parsedValue === '')) {
        return null;
      }
      if (type === 'number') {
        return parseFloat(parsedValue as string) || 0;
      }
      return parsedValue;
    },
    [parse, type, allowEmptyNumber]
  );

  const formatValue = useCallback(
    (processedValue: string | number) => {
      return format ? format(processedValue) : processedValue != null ? `${processedValue}` : '';
    },
    [format]
  );

  const [isKeyboardFocused, isFocused, setIsFocused] = useKeyboardFocus(false);
  const [state, updateState] = useObjectState(propValue, (propsValue) => {
    const initialValue = typeof propsValue !== 'undefined' ? propsValue : defaultValue;
    return {
      nonce: 0,
      value: propsValue,
      valueString: formatValue(initialValue),
    };
  });

  // Keep value in sync with the props
  useLayoutEffect(() => {
    updateState({ value: propValue });
  }, [propValue, updateState]);

  // Keep a ref for use inside the callbacks
  const stateRef = useRef<typeof state & { isFocused?: boolean }>(state);
  useLayoutEffect(() => {
    stateRef.current = { ...state, isFocused };
  }, [state, isFocused]);

  const updateInputValue = useCallback(() => {
    if (inputRef.current) {
      const nextInputValue = stateRef.current.valueString;
      const updateSelection = inputRef.current.type === 'text';
      if (updateSelection) {
        const { value: inputValue, selectionStart, selectionEnd } = inputRef.current;
        const cursorOffset = nextInputValue.length - inputValue.length || 0;
        const cursorPosition = Math.max(0, selectionEnd + cursorOffset);
        // Setting value this way rather than using value={...} avoids exposure of potentially
        // sensitive data on the DOM, eg. passwords
        inputRef.current.value = stateRef.current.valueString;
        if (selectionStart === selectionEnd) {
          try {
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          } catch (e) {
            // selection not supported
          }
        }
      } else {
        inputRef.current.value = nextInputValue;
      }
    }
  }, []);

  const handleFocus = useCallback<React.FocusEventHandler<TextFieldElement>>(
    (e) => {
      setIsFocused(true);
      onFocus && (onFocus as React.FocusEventHandler)(e);
    },
    [onFocus, setIsFocused]
  );

  const handleBlur = useCallback<React.FocusEventHandler<TextFieldElement>>(
    (e) => {
      const { value, valueString } = stateRef.current;
      const nextValue = processValue(valueString);
      const nextValueString = formatValue(nextValue);
      if (nextValue !== value && !readOnly) {
        onChange && onChange(e, nextValue);
      }
      setIsFocused(false);
      if (nextValue !== value || nextValueString !== valueString) {
        updateState({ nonce: Date.now() });
      }
      onBlur && (onBlur as React.FocusEventHandler)(e);
    },
    [processValue, formatValue, readOnly, onChange, onBlur, updateState, setIsFocused]
  );

  const handleChange = useCallback<React.ChangeEventHandler<TextFieldElement>>(
    (e) => {
      const targetValue = e.currentTarget.value;
      const nextValue = processValue(targetValue);
      const nextValueString = formatValue(nextValue);
      updateState({ valueString: targetValue });
      if (!stateRef.current.isFocused || nextValueString === targetValue) {
        onChange && onChange(e, nextValue);
      }
    },
    [processValue, formatValue, updateState, onChange]
  );

  const handleKeyDown = useCallback<React.KeyboardEventHandler<TextFieldElement>>(
    (e) => {
      if (processOnEnter && !readOnly) {
        switch (e.key) {
          case 'Enter': {
            e.preventDefault();
            const targetValue = e.currentTarget.value;
            const nextValue = processValue(targetValue);
            const nextValueString = formatValue(nextValue);
            updateState({ valueString: nextValueString });
            onChange && onChange(e, nextValue);
            break;
          }
          default:
            break;
        }
      }
      onKeyDown && (onKeyDown as React.KeyboardEventHandler)(e);
    },
    [processOnEnter, readOnly, onChange, onKeyDown, processValue, formatValue, updateState]
  );

  // When the value prop has changed, update the input (if we're allowed) or mark it as stale
  useLayoutEffect(() => {
    const nextValueString = formatValue(propValue);
    if (nextValueString !== stateRef.current.valueString) {
      if (!stateRef.current.isFocused) {
        updateState({ valueString: nextValueString, nonce: Date.now() });
      }
    }
  }, [propValue, formatValue, state.nonce, updateState]);

  // Update input value and cursor position
  useEffect(() => {
    if (inputRef.current) {
      updateInputValue();
    }
  }, [state.valueString, updateInputValue]);

  const cssVariables = {
    [`--tui-text-field-background`]: background,
    [`--tui-text-field-border`]: border,
    [`--tui-text-field-text`]: text,
    [`--tui-text-field-text-placeholder`]: textPlaceholder,
    [`--tui-text-field-icon`]: icon,
    [`--tui-text-field-focus`]: focus,
    [`--tui-text-field-background-disabled`]: backgroundDisabled,
    [`--tui-text-field-border-disabled`]: borderDisabled,
    [`--tui-text-field-border-focus`]: borderFocus,
    [`--tui-text-field-border-error`]: borderError,
    [`--tui-text-field-text-disabled`]: textDisabled,
    [`--tui-text-field-text-placeholder-disabled`]: textPlaceholderDisabled,
    [`--tui-text-field-icon-disabled`]: iconDisabled,
  } as React.CSSProperties;

  const textAreaInlineStyle = {
    minHeight,
    maxHeight,
  };

  const style = cssVariables;

  const containerProps = {
    'data-disabled': disabled ? '' : undefined,
    'data-a11y-focused': overrideFocused ?? isKeyboardFocused ? '' : undefined,
    'data-focused': overrideFocused ?? isFocused ? '' : undefined,
    'data-error': hasError ? '' : undefined,
  };

  const textFieldProps = {
    className: cx(styles.input, className),
    style: textAreaInlineStyle,
    disabled: disabled,
    readOnly: readOnly,
    type: type,
    placeholder: placeholder,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    'aria-disabled': disabled,
  };

  const getTextAreaHeight = (rows: number) =>
    `calc((${theme.lineHeight} * ${rows}) + 2 * (${theme.paddingVertical} - ${theme.borderWidth}))`;

  textAreaInlineStyle.minHeight = minHeight ? minHeight : getTextAreaHeight(minRows);
  textAreaInlineStyle.maxHeight = maxHeight
    ? maxHeight
    : maxRows
    ? getTextAreaHeight(maxRows)
    : undefined;

  const textFieldInput = multiline ? (
    <TextareaAutosize
      ref={inputRef as React.MutableRefObject<HTMLTextAreaElement>}
      {...(restProps as React.ComponentProps<typeof TextareaAutosize>)}
      {...textFieldProps}
      // See https://github.com/buildo/react-autosize-textarea/issues/137
      onChange={handleChange as React.FormEventHandler<TextFieldElement>}
    />
  ) : (
    <input
      ref={inputRef as React.MutableRefObject<HTMLInputElement>}
      {...(restProps as React.ComponentProps<'input'>)}
      {...textFieldProps}
    />
  );

  return (
    <div className={cx(styles.container, className)} style={style} {...containerProps}>
      <span className={styles.focus} />
      <div className={styles.startWrapper} />
      <div className={styles.inputWrapper}>{textFieldInput}</div>
      <div className={styles.endWrapper} />
    </div>
  );
};

export default memo(forwardRef(BaseTextFieldInput));
